// COM-B Model Readiness Assessment Questions
// Based on Implementation Science frameworks

export interface ReadinessQuestion {
  id: string;
  question: string;
  questionType: 'LIKERT' | 'YES_NO' | 'SCALE' | 'TEXT';
  domain: 'CAPABILITY' | 'OPPORTUNITY' | 'MOTIVATION';
  category: string;
  options?: string[]; // For LIKERT
  scaleMin?: number;
  scaleMax?: number;
  required?: boolean;
}

export const readinessQuestions: ReadinessQuestion[] = [
  // CAPABILITY Questions - Knowledge & Skills
  {
    id: 'cap-1',
    question: 'I understand what trust structures are and how they work.',
    questionType: 'LIKERT',
    domain: 'CAPABILITY',
    category: 'Knowledge',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },
  {
    id: 'cap-2',
    question: 'I have the financial knowledge needed to manage trust assets.',
    questionType: 'LIKERT',
    domain: 'CAPABILITY',
    category: 'Knowledge',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },
  {
    id: 'cap-3',
    question: 'I am confident in my ability to navigate legal and compliance requirements.',
    questionType: 'LIKERT',
    domain: 'CAPABILITY',
    category: 'Skills',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },
  {
    id: 'cap-4',
    question: 'I understand the timeline and milestones for this trust launch.',
    questionType: 'LIKERT',
    domain: 'CAPABILITY',
    category: 'Understanding',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },

  // OPPORTUNITY Questions - Resources & Environment
  {
    id: 'opp-1',
    question: 'I have the financial resources needed to implement this trust.',
    questionType: 'LIKERT',
    domain: 'OPPORTUNITY',
    category: 'Resources',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },
  {
    id: 'opp-2',
    question: 'I have access to professional advisors (attorney, accountant, etc.).',
    questionType: 'YES_NO',
    domain: 'OPPORTUNITY',
    category: 'Support',
    required: true
  },
  {
    id: 'opp-3',
    question: 'My current family/life situation supports this implementation.',
    questionType: 'LIKERT',
    domain: 'OPPORTUNITY',
    category: 'Context',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },
  {
    id: 'opp-4',
    question: 'I can commit the time needed for the 90-day journey.',
    questionType: 'LIKERT',
    domain: 'OPPORTUNITY',
    category: 'Time',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },

  // MOTIVATION Questions - Goals & Commitment
  {
    id: 'mot-1',
    question: 'This trust implementation aligns with my financial goals.',
    questionType: 'LIKERT',
    domain: 'MOTIVATION',
    category: 'Goals',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },
  {
    id: 'mot-2',
    question: 'I am committed to completing all tasks in the 90-day timeline.',
    questionType: 'LIKERT',
    domain: 'MOTIVATION',
    category: 'Commitment',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },
  {
    id: 'mot-3',
    question: 'I believe this trust will significantly benefit my family.',
    questionType: 'LIKERT',
    domain: 'MOTIVATION',
    category: 'Beliefs',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },
  {
    id: 'mot-4',
    question: 'The potential benefits outweigh the effort required.',
    questionType: 'LIKERT',
    domain: 'MOTIVATION',
    category: 'Value',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    required: true
  },

  // Risk Assessment
  {
    id: 'risk-1',
    question: 'What concerns do you have about this trust implementation?',
    questionType: 'TEXT',
    domain: 'OPPORTUNITY',
    category: 'Risk Assessment',
    required: false
  },
  {
    id: 'risk-2',
    question: 'What additional support do you need to be successful?',
    questionType: 'TEXT',
    domain: 'OPPORTUNITY',
    category: 'Support Needs',
    required: false
  }
];

// Helper function to calculate scores from responses
export const calculateReadinessScores = (responses: any[]) => {
  const likertMap: { [key: string]: number } = {
    'Strongly Disagree': 1,
    'Disagree': 2,
    'Neutral': 3,
    'Agree': 4,
    'Strongly Agree': 5
  };

  const yesNoMap: { [key: string]: number } = {
    'Yes': 5,
    'No': 1
  };

  const capabilityScores: number[] = [];
  const opportunityScores: number[] = [];
  const motivationScores: number[] = [];

  responses.forEach(response => {
    let value = 0;
    
    if (response.questionType === 'LIKERT' && response.answerText) {
      value = likertMap[response.answerText] || 3;
    } else if (response.questionType === 'YES_NO' && response.answerText) {
      value = yesNoMap[response.answerText] || 3;
    } else if (response.answerValue !== null && response.answerValue !== undefined) {
      value = response.answerValue;
    }

    if (response.domain === 'CAPABILITY') {
      capabilityScores.push(value);
    } else if (response.domain === 'OPPORTUNITY') {
      opportunityScores.push(value);
    } else if (response.domain === 'MOTIVATION') {
      motivationScores.push(value);
    }
  });

  const capabilityAvg = capabilityScores.length > 0
    ? Math.round((capabilityScores.reduce((a, b) => a + b, 0) / capabilityScores.length / 5) * 100)
    : 0;

  const opportunityAvg = opportunityScores.length > 0
    ? Math.round((opportunityScores.reduce((a, b) => a + b, 0) / opportunityScores.length / 5) * 100)
    : 0;

  const motivationAvg = motivationScores.length > 0
    ? Math.round((motivationScores.reduce((a, b) => a + b, 0) / motivationScores.length / 5) * 100)
    : 0;

  const overallAvg = Math.round((capabilityAvg + opportunityAvg + motivationAvg) / 3);

  return {
    capabilityScore: capabilityAvg,
    opportunityScore: opportunityAvg,
    motivationScore: motivationAvg,
    overallScore: overallAvg
  };
};

